// modules/OrderManagement/OrderSettings.jsx
import React, { useState, useEffect } from "react";
import { db } from "../../services/firebaseConfig";
import {
    doc, getDoc, setDoc, serverTimestamp, Timestamp,
    collection, getDocs, updateDoc, deleteDoc
} from "firebase/firestore";


const MY_OFFSET_MIN = 8 * 60; // Malaysia UTC+8

const pad2 = (n) => String(n).padStart(2, "0");

// Firestore Timestamp/Date -> datetime-local string ikut Malaysia
const toMYDatetimeLocal = (tsOrDate) => {
    if (!tsOrDate) return "";
    const d = tsOrDate?.toDate ? tsOrDate.toDate() : new Date(tsOrDate);

    // convert UTC -> MY by adding +8h
    const my = new Date(d.getTime() + MY_OFFSET_MIN * 60 * 1000);

    return `${my.getUTCFullYear()}-${pad2(my.getUTCMonth() + 1)}-${pad2(my.getUTCDate())}T${pad2(my.getUTCHours())}:${pad2(my.getUTCMinutes())}`;
};

// datetime-local string (dianggap MY time) -> Date UTC sebenar
const fromMYDatetimeLocal = (value) => {
    if (!value) return null;
    const [datePart, timePart] = value.split("T");
    const [y, m, d] = datePart.split("-").map(Number);
    const [hh, mm] = timePart.split(":").map(Number);

    // MY time -> UTC time (tolak 8 jam)
    const utcMs = Date.UTC(y, m - 1, d, hh - 8, mm, 0);
    return new Date(utcMs);
};


const OrderSettings = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [openDate, setOpenDate] = useState('');
    const [closeDate, setCloseDate] = useState('');
    const [loading, setLoading] = useState(true);
    const [resetting, setResetting] = useState(false);

    useEffect(() => {
        const fetchSettings = async () => {
            const ref = doc(db, "inventori", "selangor", "settings", "order");
            const snap = await getDoc(ref);
            if (snap.exists()) {
                const data = snap.data();
                setIsOpen(data.isOpen ?? false);
                setOpenDate(data.openDate ? toMYDatetimeLocal(data.openDate) : "");
                setCloseDate(data.closeDate ? toMYDatetimeLocal(data.closeDate) : "");

            }
            setLoading(false);
        };
        fetchSettings();
    }, []);

    const saveSettings = async () => {
        const ref = doc(db, "inventori", "selangor", "settings", "order");
        await setDoc(ref, {
            isOpen,
            openDate: openDate ? Timestamp.fromDate(fromMYDatetimeLocal(openDate)) : null,
            closeDate: closeDate ? Timestamp.fromDate(fromMYDatetimeLocal(closeDate)) : null,

            updatedAt: serverTimestamp()
        }, { merge: true });
        alert("Tetapan berjaya disimpan!");
    };

    // ===== Helper: pre-load peta stok (id & name) sekali =====
    const loadStockMaps = async () => {
        const stocksCol = collection(db, "inventori", "selangor", "stocks");
        const snap = await getDocs(stocksCol);
        const byId = new Map();
        const byName = new Map();
        snap.forEach(s => {
            const d = s.data();
            byId.set(s.id, { id: s.id, data: d });
            if (d.name) byName.set(d.name, { id: s.id, data: d });
        });
        return { byId, byName };
    };

    // ===== Helper: tambah nombor dengan selamat =====
    const safeAdd = (a, b) => Number(a || 0) + Number(b || 0);

    // ===== Pulihkan stok dari 1 order (guna map stok sedia ada) =====
    const restoreStockFromOrder = async (order, stockMaps) => {
        const items = Array.isArray(order.items) ? order.items : [];
        for (const item of items) {
            // Cari stok ikut id dahulu, kalau tak ada, ikut name
            let stockEntry = null;
            if (item.id && stockMaps.byId.has(item.id)) stockEntry = stockMaps.byId.get(item.id);
            else if (item.name && stockMaps.byName.has(item.name)) stockEntry = stockMaps.byName.get(item.name);
            if (!stockEntry) continue;

            const stockRef = doc(db, "inventori", "selangor", "stocks", stockEntry.id);
            const stockSnap = await getDoc(stockRef);
            if (!stockSnap.exists()) continue;

            const stockData = stockSnap.data();
            const updated = {};

            // Kes 1: struktur baru simpan ke item.hargaSnapshot
            if (item.hargaSnapshot) {
                if (typeof item.hargaSnapshot.qty === "number") {
                    // non-split (buku/runcit kuantiti tunggal)
                    if (typeof stockData.quantity === "number") {
                        updated.quantity = safeAdd(stockData.quantity, item.hargaSnapshot.qty);
                    }
                } else {
                    // split varian (baju sizes / buku years)
                    if (stockData.sizeQuantities && typeof stockData.sizeQuantities === "object") {
                        const newSizes = { ...stockData.sizeQuantities };
                        Object.entries(item.hargaSnapshot).forEach(([variant, val]) => {
                            const qty = Number(val?.qty || 0);
                            if (qty) newSizes[variant] = safeAdd(newSizes[variant], qty);
                        });
                        updated.sizeQuantities = newSizes;
                    } else if (stockData.yearQuantities && typeof stockData.yearQuantities === "object") {
                        const newYears = { ...stockData.yearQuantities };
                        Object.entries(item.hargaSnapshot).forEach(([variant, val]) => {
                            const qty = Number(val?.qty || 0);
                            if (qty) newYears[variant] = safeAdd(newYears[variant], qty);
                        });
                        updated.yearQuantities = newYears;
                    } else if (typeof stockData.quantity === "number") {
                        const totalQty = Object.values(item.hargaSnapshot).reduce((s, v) => s + Number(v?.qty || 0), 0);
                        updated.quantity = safeAdd(stockData.quantity, totalQty);
                    }
                }
            }
            // Kes 2: struktur lama – quantity / sizes / years
            else if (item.quantity && typeof stockData.quantity === "number") {
                updated.quantity = safeAdd(stockData.quantity, item.quantity);
            } else if (item.sizes && stockData.sizeQuantities) {
                const newSizes = { ...stockData.sizeQuantities };
                Object.entries(item.sizes).forEach(([size, qty]) => {
                    newSizes[size] = safeAdd(newSizes[size], qty);
                });
                updated.sizeQuantities = newSizes;
            } else if (item.years && stockData.yearQuantities) {
                const newYears = { ...stockData.yearQuantities };
                Object.entries(item.years).forEach(([year, qty]) => {
                    newYears[year] = safeAdd(newYears[year], qty);
                });
                updated.yearQuantities = newYears;
            }

            if (Object.keys(updated).length) {
                await updateDoc(stockRef, updated);
            }
        }
    };

    // ====== BUTANG ADMIN: Pulihkan stok + bersihkan semua order (DEV ONLY) ======
    const handleResetOrdersAndStock = async () => {
        const ok1 = window.confirm("Tindakan ini akan memulihkan stok dan memadam SEMUA order & compiledOrders. Sesuai untuk data percubaan/dev. Teruskan?");
        if (!ok1) return;
        const ok2 = window.prompt('Taip "RESET" untuk sahkan:');
        if (ok2 !== "RESET") return;

        setResetting(true);
        try {
            // 1) Preload stok sekali
            const stockMaps = await loadStockMaps();

            // 2) Ambil semua orders
            const ordersCol = collection(db, "inventori", "selangor", "orders");
            const ordersSnap = await getDocs(ordersCol);

            // 3) Pulihkan stok & padam order satu-persatu
            for (const d of ordersSnap.docs) {
                const order = { id: d.id, ...d.data() };
                // Pulihkan stok untuk SEMUA status (sebab environment percubaan)
                // Jika mahu "selamat", tapis di sini: if (order.status === "ORDER_CONFIRMED") { ... }
                // eslint-disable-next-line no-await-in-loop
                await restoreStockFromOrder(order, stockMaps);
                // eslint-disable-next-line no-await-in-loop
                await deleteDoc(doc(db, "inventori", "selangor", "orders", d.id));
            }

            // 4) Padam semua compiledOrders (tiada perubahan stok)
            const compiledCol = collection(db, "inventori", "selangor", "compiledOrders");
            const compiledSnap = await getDocs(compiledCol);
            for (const d of compiledSnap.docs) {
                // eslint-disable-next-line no-await-in-loop
                await deleteDoc(doc(db, "inventori", "selangor", "compiledOrders", d.id));
            }

            alert("Selesai: Stok dipulihkan & semua order (termasuk compiledOrders) dipadam.");
        } catch (e) {
            console.error("Reset gagal:", e);
            alert("Reset gagal. Semak console untuk butiran.");
        } finally {
            setResetting(false);
        }
    };

    if (loading) return <div>Memuatkan tetapan...</div>;

    return (
        <div className="order-settings-container">
            <h2>Tetapan</h2>

            <label>
                Tempahan:
                <select value={isOpen ? "open" : "close"} onChange={e => setIsOpen(e.target.value === "open")}>
                    <option value="open">Buka</option>
                    <option value="close">Tutup</option>
                </select>
            </label>

            <br /><br />
            <label>
                Tarikh & Masa Buka:
                <input type="datetime-local" value={openDate} onChange={e => setOpenDate(e.target.value)} />
            </label>
            <br /><br />
            <label>
                Tarikh & Masa Tutup:
                <input type="datetime-local" value={closeDate} onChange={e => setCloseDate(e.target.value)} />
            </label>
            <br /><br />

            <button className="submit-button" onClick={saveSettings}>Simpan</button>

            {/* ===== Butang ADMIN DEV: Reset data percubaan ===== */}
            <div style={{ marginTop: 24, paddingTop: 12, borderTop: "1px solid #eee" }}>
                <button
                    className="submit-button"
                    style={{ backgroundColor: "red" }}
                    onClick={handleResetOrdersAndStock}
                    disabled={resetting}
                    title="Pulihkan stok & padam semua orders + compiledOrders"
                >
                    {resetting ? "Memproses..." : "Reset Order"}
                </button>
                <p style={{ color: "#b71c1c", marginTop: 8, fontSize: 13 }}>
                    Amaran: Untuk environment percubaan/dev sahaja. Jangan guna di production.
                </p>
            </div>
        </div>
    );
};

export default OrderSettings;
